import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Activity,
    Heart,
    Footprints,
    Moon,
    Flame,
    Watch,
    Smartphone,
    Link,
    Unlink,
    RefreshCw,
    TrendingUp,
    Clock,
    Calendar,
    Loader2,
    CheckCircle,
    AlertCircle,
    Settings,
    ChevronRight,
    Zap,
    Target,
    BarChart3,
} from 'lucide-react';

interface WearableConnection {
    id: string;
    provider: string;
    provider_user_id: string;
    is_active: boolean;
    last_sync_at: string | null;
    created_at: string;
}

interface SensorData {
    id: string;
    data_type: string;
    value: number;
    unit: string;
    recorded_at: string;
    source: string;
}

interface DailyStats {
    steps: number;
    calories: number;
    active_minutes: number;
    sleep_hours: number;
    heart_rate_avg: number;
    heart_rate_max: number;
}

const PROVIDERS = [
    {
        id: 'google_fit',
        name: 'Google Fit',
        icon: '🏃',
        color: 'bg-blue-500',
        description: 'Sincronize dados de atividade, sono e frequência cardíaca',
    },
    {
        id: 'apple_health',
        name: 'Apple Health',
        icon: '🍎',
        color: 'bg-gray-800',
        description: 'Conecte seu iPhone ou Apple Watch',
    },
    {
        id: 'garmin',
        name: 'Garmin',
        icon: '⌚',
        color: 'bg-green-600',
        description: 'Sincronize com seu dispositivo Garmin',
    },
    {
        id: 'fitbit',
        name: 'Fitbit',
        icon: '💪',
        color: 'bg-teal-500',
        description: 'Conecte sua pulseira ou smartwatch Fitbit',
    },
    {
        id: 'samsung_health',
        name: 'Samsung Health',
        icon: '📱',
        color: 'bg-blue-700',
        description: 'Sincronize com seu dispositivo Samsung',
    },
    {
        id: 'strava',
        name: 'Strava',
        icon: '🚴',
        color: 'bg-orange-500',
        description: 'Importe atividades de corrida e ciclismo',
    },
];

const DATA_TYPES = {
    steps: { label: 'Passos', icon: Footprints, color: 'text-blue-500', unit: 'passos' },
    calories: { label: 'Calorias', icon: Flame, color: 'text-orange-500', unit: 'kcal' },
    heart_rate: { label: 'Freq. Cardíaca', icon: Heart, color: 'text-red-500', unit: 'bpm' },
    sleep: { label: 'Sono', icon: Moon, color: 'text-purple-500', unit: 'horas' },
    active_minutes: { label: 'Ativo', icon: Activity, color: 'text-green-500', unit: 'min' },
    distance: { label: 'Distância', icon: TrendingUp, color: 'text-cyan-500', unit: 'km' },
};

export function WearablesPage() {
    const { profile } = useAuth();
    const queryClient = useQueryClient();
    const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'connections' | 'data' | 'settings'>('connections');

    // Fetch connections
    const { data: connections, isLoading: loadingConnections } = useQuery({
        queryKey: ['wearable-connections', profile?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('wearable_connections')
                .select('*')
                .eq('user_id', profile?.id);

            if (error) throw error;
            return data as WearableConnection[];
        },
        enabled: !!profile?.id,
    });

    // Fetch recent data
    const { data: recentData, isLoading: loadingData } = useQuery({
        queryKey: ['sensor-data', profile?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('sensor_data')
                .select('*')
                .eq('user_id', profile?.id)
                .gte('recorded_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
                .order('recorded_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            return data as SensorData[];
        },
        enabled: !!profile?.id,
    });

    // Connect provider mutation
    const connectMutation = useMutation({
        mutationFn: async (provider: string) => {
            // In production, this would redirect to OAuth flow
            // For demo, we simulate connection
            const { error } = await supabase
                .from('wearable_connections')
                .insert({
                    user_id: profile?.id,
                    provider,
                    provider_user_id: `demo_${Date.now()}`,
                    access_token: 'demo_token',
                    is_active: true,
                });

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wearable-connections'] });
            setSelectedProvider(null);
        },
    });

    // Disconnect mutation
    const disconnectMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('wearable_connections')
                .delete()
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wearable-connections'] });
        },
    });

    // Sync mutation
    const syncMutation = useMutation({
        mutationFn: async (connectionId: string) => {
            // Simulate sync with demo data
            const demoData = [
                { data_type: 'steps', value: Math.floor(Math.random() * 10000) + 3000, unit: 'steps' },
                { data_type: 'calories', value: Math.floor(Math.random() * 500) + 1500, unit: 'kcal' },
                { data_type: 'heart_rate', value: Math.floor(Math.random() * 30) + 60, unit: 'bpm' },
                { data_type: 'active_minutes', value: Math.floor(Math.random() * 60) + 20, unit: 'minutes' },
            ];

            for (const data of demoData) {
                await supabase.from('sensor_data').insert({
                    user_id: profile?.id,
                    connection_id: connectionId,
                    ...data,
                    source: 'demo',
                    recorded_at: new Date().toISOString(),
                });
            }

            await supabase
                .from('wearable_connections')
                .update({ last_sync_at: new Date().toISOString() })
                .eq('id', connectionId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wearable-connections'] });
            queryClient.invalidateQueries({ queryKey: ['sensor-data'] });
        },
    });

    // Calculate today's stats
    const todayStats: DailyStats = {
        steps: recentData?.filter((d) => d.data_type === 'steps')
            .reduce((max, d) => Math.max(max, d.value), 0) || 0,
        calories: recentData?.filter((d) => d.data_type === 'calories')
            .reduce((max, d) => Math.max(max, d.value), 0) || 0,
        active_minutes: recentData?.filter((d) => d.data_type === 'active_minutes')
            .reduce((sum, d) => sum + d.value, 0) || 0,
        sleep_hours: recentData?.filter((d) => d.data_type === 'sleep')
            .reduce((_, d) => d.value, 0) || 0,
        heart_rate_avg: Math.round(
            (recentData?.filter((d) => d.data_type === 'heart_rate')
                .reduce((sum, d, _, arr) => sum + d.value / arr.length, 0)) || 0
        ),
        heart_rate_max: recentData?.filter((d) => d.data_type === 'heart_rate')
            .reduce((max, d) => Math.max(max, d.value), 0) || 0,
    };

    const connectedProviders = connections?.map((c) => c.provider) || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <Watch className="w-6 h-6" />
                        Wearables & Sensores
                    </h1>
                    <p className="section-subtitle">
                        Conecte seus dispositivos para acompanhar seu progresso
                    </p>
                </div>
            </div>

            {/* Today's Stats */}
            <div className="grid gap-4 md:grid-cols-6">
                <StatCard
                    icon={Footprints}
                    label="Passos"
                    value={todayStats.steps.toLocaleString()}
                    color="blue"
                    goal={10000}
                    current={todayStats.steps}
                />
                <StatCard
                    icon={Flame}
                    label="Calorias"
                    value={`${todayStats.calories}`}
                    unit="kcal"
                    color="orange"
                />
                <StatCard
                    icon={Activity}
                    label="Ativo"
                    value={`${todayStats.active_minutes}`}
                    unit="min"
                    color="green"
                    goal={30}
                    current={todayStats.active_minutes}
                />
                <StatCard
                    icon={Moon}
                    label="Sono"
                    value={`${todayStats.sleep_hours}`}
                    unit="h"
                    color="purple"
                />
                <StatCard
                    icon={Heart}
                    label="BPM Médio"
                    value={`${todayStats.heart_rate_avg}`}
                    unit="bpm"
                    color="red"
                />
                <StatCard
                    icon={Heart}
                    label="BPM Max"
                    value={`${todayStats.heart_rate_max}`}
                    unit="bpm"
                    color="pink"
                />
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b">
                <button
                    onClick={() => setActiveTab('connections')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'connections'
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Link className="w-4 h-4 inline mr-2" />
                    Conexões
                </button>
                <button
                    onClick={() => setActiveTab('data')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'data'
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <BarChart3 className="w-4 h-4 inline mr-2" />
                    Dados
                </button>
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'settings'
                            ? 'border-primary-600 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Configurações
                </button>
            </div>

            {/* Connections Tab */}
            {activeTab === 'connections' && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {PROVIDERS.map((provider) => {
                        const connection = connections?.find((c) => c.provider === provider.id);
                        const isConnected = !!connection;

                        return (
                            <div
                                key={provider.id}
                                className={`card hover:shadow-lg transition-all ${isConnected ? 'ring-2 ring-green-500' : ''
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-12 h-12 rounded-xl ${provider.color} flex items-center justify-center text-2xl`}
                                        >
                                            {provider.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold">{provider.name}</h3>
                                            {isConnected ? (
                                                <span className="text-xs text-green-600 flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />
                                                    Conectado
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-500">
                                                    Não conectado
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-600 mb-4">
                                    {provider.description}
                                </p>

                                {isConnected && connection?.last_sync_at && (
                                    <p className="text-xs text-gray-500 mb-4">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        Última sincronização: {new Date(connection.last_sync_at).toLocaleString('pt-BR')}
                                    </p>
                                )}

                                <div className="flex gap-2">
                                    {isConnected ? (
                                        <>
                                            <button
                                                onClick={() => syncMutation.mutate(connection!.id)}
                                                disabled={syncMutation.isPending}
                                                className="btn-secondary flex-1 text-sm"
                                            >
                                                {syncMutation.isPending ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4 mr-1" />
                                                )}
                                                Sincronizar
                                            </button>
                                            <button
                                                onClick={() => disconnectMutation.mutate(connection!.id)}
                                                className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                                            >
                                                <Unlink className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => connectMutation.mutate(provider.id)}
                                            disabled={connectMutation.isPending}
                                            className="btn-primary flex-1 text-sm"
                                        >
                                            <Link className="w-4 h-4 mr-1" />
                                            Conectar
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Data Tab */}
            {activeTab === 'data' && (
                <div className="space-y-6">
                    {loadingData ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                    ) : recentData && recentData.length > 0 ? (
                        <div className="card">
                            <h3 className="font-semibold mb-4">Dados Recentes (7 dias)</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b text-left text-sm text-gray-500">
                                            <th className="pb-3 font-medium">Tipo</th>
                                            <th className="pb-3 font-medium">Valor</th>
                                            <th className="pb-3 font-medium">Fonte</th>
                                            <th className="pb-3 font-medium">Data</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {recentData.slice(0, 20).map((data) => {
                                            const config = DATA_TYPES[data.data_type as keyof typeof DATA_TYPES];
                                            return (
                                                <tr key={data.id} className="hover:bg-gray-50">
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-2">
                                                            {config && <config.icon className={`w-4 h-4 ${config.color}`} />}
                                                            <span>{config?.label || data.data_type}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 font-medium">
                                                        {data.value.toLocaleString()} {data.unit}
                                                    </td>
                                                    <td className="py-3 text-sm text-gray-500">{data.source}</td>
                                                    <td className="py-3 text-sm text-gray-500">
                                                        {new Date(data.recorded_at).toLocaleDateString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="card text-center py-12">
                            <Activity className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <h3 className="font-medium text-gray-900 mb-2">Sem dados ainda</h3>
                            <p className="text-gray-500">
                                Conecte um dispositivo e sincronize para ver seus dados
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="card max-w-lg">
                    <h3 className="font-semibold mb-4">Configurações de Sincronização</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between py-3 border-b">
                            <div>
                                <p className="font-medium">Sincronização Automática</p>
                                <p className="text-sm text-gray-500">Sincronizar dados a cada 6 horas</p>
                            </div>
                            <input type="checkbox" defaultChecked className="toggle" />
                        </div>
                        <div className="flex items-center justify-between py-3 border-b">
                            <div>
                                <p className="font-medium">Notificações de Meta</p>
                                <p className="text-sm text-gray-500">Avisar quando atingir metas diárias</p>
                            </div>
                            <input type="checkbox" defaultChecked className="toggle" />
                        </div>
                        <div className="flex items-center justify-between py-3">
                            <div>
                                <p className="font-medium">Compartilhar com Personal</p>
                                <p className="text-sm text-gray-500">Permitir que seu personal veja os dados</p>
                            </div>
                            <input type="checkbox" defaultChecked className="toggle" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Stat Card Component
function StatCard({
    icon: Icon,
    label,
    value,
    unit,
    color,
    goal,
    current,
}: {
    icon: React.ElementType;
    label: string;
    value: string;
    unit?: string;
    color: string;
    goal?: number;
    current?: number;
}) {
    const colorClasses: Record<string, string> = {
        blue: 'bg-blue-100 text-blue-600',
        orange: 'bg-orange-100 text-orange-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        red: 'bg-red-100 text-red-600',
        pink: 'bg-pink-100 text-pink-600',
    };

    const progress = goal && current ? Math.min((current / goal) * 100, 100) : null;

    return (
        <div className="card">
            <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <span className="text-xs text-gray-500">{label}</span>
            </div>
            <p className="text-2xl font-bold">
                {value}
                {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
            </p>
            {progress !== null && (
                <div className="mt-2">
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full ${color === 'blue' ? 'bg-blue-500' : color === 'green' ? 'bg-green-500' : 'bg-primary-500'}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {Math.round(progress)}% da meta
                    </p>
                </div>
            )}
        </div>
    );
}
